/**
 * BudgetExportShare Component
 *
 * Sistema de exportação e compartilhamento de orçamentos
 */

import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Download,
  Share2,
  Mail,
  MessageSquare,
  Link,
  FileText,
  FileSpreadsheet,
  Printer,
  Eye,
  Copy,
  QrCode,
  Smartphone,
  Globe,
  Lock,
  Unlock,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Settings,
  Zap,
  ExternalLink,
  RefreshCw,
  Shield,
  User,
  Users,
  Bell,
  BellOff,
  Trash2,
  Edit,
  Star
} from 'lucide-react';

interface BudgetExportShareProps {
  budget: {
    id: string;
    number: string;
    client: {
      name: string;
      email: string;
    };
    total: number;
    status: string;
    items: any[];
  };
  onExportPDF?: (options: PDFOptions) => void;
  onExportExcel?: (options: ExcelOptions) => void;
  onShareLink?: (options: ShareLinkOptions) => void;
  onSendEmail?: (options: EmailOptions) => void;
  onSendWhatsApp?: (options: WhatsAppOptions) => void;
  onPrint?: (options: PrintOptions) => void;
  onGenerateQrCode?: () => void;
  allowPublicShare?: boolean;
  allowEmailShare?: boolean;
  allowWhatsAppShare?: boolean;
  allowExport?: boolean;
  allowPrint?: boolean;
  className?: string;
}

interface PDFOptions {
  includeHeader: boolean;
  includeFooter: boolean;
  includeLogo: boolean;
  includeTerms: boolean;
  includeSignature: boolean;
  watermark?: string;
  password?: string;
  format: 'A4' | 'Letter';
  orientation: 'portrait' | 'landscape';
  language: 'pt-BR' | 'en-US';
}

interface ExcelOptions {
  includeFormulas: boolean;
  includeCharts: boolean;
  groupByCategory: boolean;
  includeSummary: boolean;
  format: 'xlsx' | 'csv';
}

interface ShareLinkOptions {
  public: boolean;
  password?: string;
  expiresAt?: string;
  allowDownload: boolean;
  allowComments: boolean;
  allowPrint: boolean;
  tracking: boolean;
}

interface EmailOptions {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  message: string;
  attachPDF: boolean;
  attachExcel: boolean;
  trackOpens: boolean;
  template?: string;
}

interface WhatsAppOptions {
  phone: string;
  message: string;
  includeLink: boolean;
  includePDF: boolean;
  includeSummary: boolean;
}

interface PrintOptions {
  includeHeader: boolean;
    includeFooter: boolean;
    includeLogo: boolean;
    includeTerms: boolean;
    copies: number;
    format: 'A4' | 'Letter';
    orientation: 'portrait' | 'landscape';
}

export function BudgetExportShare({
  budget,
  onExportPDF,
  onExportExcel,
  onShareLink,
  onSendEmail,
  onSendWhatsApp,
  onPrint,
  onGenerateQrCode,
  allowPublicShare = true,
  allowEmailShare = true,
  allowWhatsAppShare = true,
  allowExport = true,
  allowPrint = true,
  className
}: BudgetExportShareProps) {
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsAppDialogOpen] = useState(false);
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);

  // Export options
  const [pdfOptions, setPdfOptions] = useState<PDFOptions>({
    includeHeader: true,
    includeFooter: true,
    includeLogo: true,
    includeTerms: true,
    includeSignature: false,
    format: 'A4',
    orientation: 'portrait',
    language: 'pt-BR'
  });

  const [excelOptions, setExcelOptions] = useState<ExcelOptions>({
    includeFormulas: true,
    includeCharts: false,
    groupByCategory: true,
    includeSummary: true,
    format: 'xlsx'
  });

  const [printOptions, setPrintOptions] = useState<PrintOptions>({
    includeHeader: true,
    includeFooter: true,
    includeLogo: true,
    includeTerms: true,
    copies: 1,
    format: 'A4',
    orientation: 'portrait'
  });

  // Share options
  const [shareLinkOptions, setShareLinkOptions] = useState<ShareLinkOptions>({
    public: false,
    allowDownload: true,
    allowComments: false,
    allowPrint: true,
    tracking: true
  });

  const [generatedLink, setGeneratedLink] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState(false);

  // Email options
  const [emailOptions, setEmailOptions] = useState<EmailOptions>({
    to: [budget.client.email],
    subject: `Orçamento ${budget.number} - ${budget.client.name}`,
    message: `Prezado(a) ${budget.client.name},\n\nSegue em anexo o orçamento ${budget.number} conforme solicitado.\n\nFicamos à disposição para quaisquer dúvidas.\n\nAtenciosamente,`,
    attachPDF: true,
    attachExcel: false,
    trackOpens: true
  });

  // WhatsApp options
  const [whatsappOptions, setWhatsAppOptions] = useState<WhatsAppOptions>({
    phone: '',
    message: `Olá ${budget.client.name}! Segue seu orçamento ${budget.number}. Valor total: R$ ${budget.total.toLocaleString('pt-BR')}.`,
    includeLink: true,
    includePDF: false,
    includeSummary: true
  });

  const handleExportPDF = () => {
    onExportPDF?.(pdfOptions);
    setExportDialogOpen(false);
  };

  const handleExportExcel = () => {
    onExportExcel?.(excelOptions);
    setExportDialogOpen(false);
  };

  const handlePrint = () => {
    onPrint?.(printOptions);
    setExportDialogOpen(false);
  };

  const handleGenerateShareLink = () => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/budgets/shared/${budget.id}`;
    setGeneratedLink(link);
    onShareLink?.(shareLinkOptions);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleSendEmail = () => {
    onSendEmail?.(emailOptions);
    setEmailDialogOpen(false);
  };

  const handleSendWhatsApp = () => {
    const message = encodeURIComponent(whatsappOptions.message);
    const url = `https://wa.me/${whatsappOptions.phone.replace(/\D/g, '')}?text=${message}`;
    window.open(url, '_blank');
    onSendWhatsApp?.(whatsappOptions);
    setWhatsAppDialogOpen(false);
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  return (
    <div className={className}>
      {/* Export Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {allowExport && (
            <>
              <DropdownMenuItem onClick={() => setExportDialogOpen(true)}>
                <FileText className="h-4 w-4 mr-2" />
                Exportar PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setExportDialogOpen(true)}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Exportar Excel
              </DropdownMenuItem>
            </>
          )}
          {allowPrint && (
            <DropdownMenuItem onClick={() => setExportDialogOpen(true)}>
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Share Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Share2 className="h-4 w-4 mr-2" />
            Compartilhar
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {allowPublicShare && (
            <DropdownMenuItem onClick={() => setShareDialogOpen(true)}>
              <Link className="h-4 w-4 mr-2" />
              Gerar Link
            </DropdownMenuItem>
          )}
          {allowEmailShare && (
            <DropdownMenuItem onClick={() => setEmailDialogOpen(true)}>
              <Mail className="h-4 w-4 mr-2" />
              Enviar por Email
            </DropdownMenuItem>
          )}
          {allowWhatsAppShare && (
            <DropdownMenuItem onClick={() => setWhatsAppDialogOpen(true)}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Enviar por WhatsApp
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setQrCodeDialogOpen(true)}>
            <QrCode className="h-4 w-4 mr-2" />
            Gerar QR Code
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Exportar Orçamento
            </DialogTitle>
            <DialogDescription>
              Escolha o formato e opções de exportação para o orçamento {budget.number}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="pdf" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="pdf">PDF</TabsTrigger>
              <TabsTrigger value="excel">Excel</TabsTrigger>
              <TabsTrigger value="print">Impressão</TabsTrigger>
            </TabsList>

            <TabsContent value="pdf" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Formato</Label>
                  <Select
                    value={pdfOptions.format}
                    onValueChange={(value: 'A4' | 'Letter') => setPdfOptions({ ...pdfOptions, format: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4">A4</SelectItem>
                      <SelectItem value="Letter">Carta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Orientação</Label>
                  <Select
                    value={pdfOptions.orientation}
                    onValueChange={(value: 'portrait' | 'landscape') => setPdfOptions({ ...pdfOptions, orientation: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portrait">Retrato</SelectItem>
                      <SelectItem value="landscape">Paisagem</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Idioma</Label>
                <Select
                  value={pdfOptions.language}
                  onValueChange={(value: 'pt-BR' | 'en-US') => setPdfOptions({ ...pdfOptions, language: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                    <SelectItem value="en-US">English (US)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Opções de Conteúdo</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="pdf-header"
                      checked={pdfOptions.includeHeader}
                      onCheckedChange={(checked) => setPdfOptions({ ...pdfOptions, includeHeader: checked })}
                    />
                    <Label htmlFor="pdf-header">Incluir cabeçalho</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="pdf-footer"
                      checked={pdfOptions.includeFooter}
                      onCheckedChange={(checked) => setPdfOptions({ ...pdfOptions, includeFooter: checked })}
                    />
                    <Label htmlFor="pdf-footer">Incluir rodapé</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="pdf-logo"
                      checked={pdfOptions.includeLogo}
                      onCheckedChange={(checked) => setPdfOptions({ ...pdfOptions, includeLogo: checked })}
                    />
                    <Label htmlFor="pdf-logo">Incluir logo</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="pdf-terms"
                      checked={pdfOptions.includeTerms}
                      onCheckedChange={(checked) => setPdfOptions({ ...pdfOptions, includeTerms: checked })}
                    />
                    <Label htmlFor="pdf-terms">Incluir termos</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="pdf-signature"
                      checked={pdfOptions.includeSignature}
                      onCheckedChange={(checked) => setPdfOptions({ ...pdfOptions, includeSignature: checked })}
                    />
                    <Label htmlFor="pdf-signature">Campo de assinatura</Label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Marca d'água (opcional)</Label>
                  <Input
                    value={pdfOptions.watermark || ''}
                    onChange={(e) => setPdfOptions({ ...pdfOptions, watermark: e.target.value })}
                    placeholder="Ex: Rascunho"
                  />
                </div>
                <div>
                  <Label>Senha (opcional)</Label>
                  <Input
                    type="password"
                    value={pdfOptions.password || ''}
                    onChange={(e) => setPdfOptions({ ...pdfOptions, password: e.target.value })}
                    placeholder="Proteger com senha"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleExportPDF}>
                  <FileText className="h-4 w-4 mr-2" />
                  Exportar PDF
                </Button>
              </DialogFooter>
            </TabsContent>

            <TabsContent value="excel" className="space-y-4">
              <div>
                <Label>Formato</Label>
                <Select
                  value={excelOptions.format}
                  onValueChange={(value: 'xlsx' | 'csv') => setExcelOptions({ ...excelOptions, format: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Opções de Conteúdo</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="excel-formulas"
                      checked={excelOptions.includeFormulas}
                      onCheckedChange={(checked) => setExcelOptions({ ...excelOptions, includeFormulas: checked })}
                    />
                    <Label htmlFor="excel-formulas">Incluir fórmulas</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="excel-charts"
                      checked={excelOptions.includeCharts}
                      onCheckedChange={(checked) => setExcelOptions({ ...excelOptions, includeCharts: checked })}
                    />
                    <Label htmlFor="excel-charts">Incluir gráficos</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="excel-group"
                      checked={excelOptions.groupByCategory}
                      onCheckedChange={(checked) => setExcelOptions({ ...excelOptions, groupByCategory: checked })}
                    />
                    <Label htmlFor="excel-group">Agrupar por categoria</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="excel-summary"
                      checked={excelOptions.includeSummary}
                      onCheckedChange={(checked) => setExcelOptions({ ...excelOptions, includeSummary: checked })}
                    />
                    <Label htmlFor="excel-summary">Incluir resumo</Label>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleExportExcel}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Exportar Excel
                </Button>
              </DialogFooter>
            </TabsContent>

            <TabsContent value="print" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Formato</Label>
                  <Select
                    value={printOptions.format}
                    onValueChange={(value: 'A4' | 'Letter') => setPrintOptions({ ...printOptions, format: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4">A4</SelectItem>
                      <SelectItem value="Letter">Carta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Orientação</Label>
                  <Select
                    value={printOptions.orientation}
                    onValueChange={(value: 'portrait' | 'landscape') => setPrintOptions({ ...printOptions, orientation: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portrait">Retrato</SelectItem>
                      <SelectItem value="landscape">Paisagem</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Número de cópias</Label>
                <Input
                  type="number"
                  value={printOptions.copies}
                  onChange={(e) => setPrintOptions({ ...printOptions, copies: parseInt(e.target.value) || 1 })}
                  min="1"
                  max="10"
                />
              </div>

              <div className="space-y-3">
                <Label>Opções de Conteúdo</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="print-header"
                      checked={printOptions.includeHeader}
                      onCheckedChange={(checked) => setPrintOptions({ ...printOptions, includeHeader: checked })}
                    />
                    <Label htmlFor="print-header">Incluir cabeçalho</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="print-footer"
                      checked={printOptions.includeFooter}
                      onCheckedChange={(checked) => setPrintOptions({ ...printOptions, includeFooter: checked })}
                    />
                    <Label htmlFor="print-footer">Incluir rodapé</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="print-logo"
                      checked={printOptions.includeLogo}
                      onCheckedChange={(checked) => setPrintOptions({ ...printOptions, includeLogo: checked })}
                    />
                    <Label htmlFor="print-logo">Incluir logo</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="print-terms"
                      checked={printOptions.includeTerms}
                      onCheckedChange={(checked) => setPrintOptions({ ...printOptions, includeTerms: checked })}
                    />
                    <Label htmlFor="print-terms">Incluir termos</Label>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handlePrint}>
                  <Printer className="h-4 w-4 mr-2" />
                  Imprimir
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Share Link Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="h-5 w-5" />
              Compartilhar Link
            </DialogTitle>
            <DialogDescription>
              Crie um link para compartilhar este orçamento
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Link Público</Label>
                <p className="text-xs text-muted-foreground">
                  Qualquer pessoa com o link pode visualizar
                </p>
              </div>
              <Switch
                checked={shareLinkOptions.public}
                onCheckedChange={(checked) => setShareLinkOptions({ ...shareLinkOptions, public: checked })}
              />
            </div>

            {!shareLinkOptions.public && (
              <div>
                <Label>Senha de Acesso</Label>
                <Input
                  type="password"
                  value={shareLinkOptions.password || ''}
                  onChange={(e) => setShareLinkOptions({ ...shareLinkOptions, password: e.target.value })}
                  placeholder="Senha opcional"
                />
              </div>
            )}

            <div>
              <Label>Data de Expiração (opcional)</Label>
              <Input
                type="date"
                value={shareLinkOptions.expiresAt || ''}
                onChange={(e) => setShareLinkOptions({ ...shareLinkOptions, expiresAt: e.target.value })}
              />
            </div>

            <div className="space-y-3">
              <Label>Permissões</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Permitir download</span>
                  <Switch
                    checked={shareLinkOptions.allowDownload}
                    onCheckedChange={(checked) => setShareLinkOptions({ ...shareLinkOptions, allowDownload: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Permitir comentários</span>
                  <Switch
                    checked={shareLinkOptions.allowComments}
                    onCheckedChange={(checked) => setShareLinkOptions({ ...shareLinkOptions, allowComments: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Permitir impressão</span>
                  <Switch
                    checked={shareLinkOptions.allowPrint}
                    onCheckedChange={(checked) => setShareLinkOptions({ ...shareLinkOptions, allowPrint: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Rastrear acessos</span>
                  <Switch
                    checked={shareLinkOptions.tracking}
                    onCheckedChange={(checked) => setShareLinkOptions({ ...shareLinkOptions, tracking: checked })}
                  />
                </div>
              </div>
            </div>

            {generatedLink && (
              <div className="space-y-2">
                <Label>Link Gerado</Label>
                <div className="flex gap-2">
                  <Input
                    value={generatedLink}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={handleCopyLink}
                    className="shrink-0"
                  >
                    {linkCopied ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {linkCopied && (
                  <p className="text-sm text-green-600">Link copiado para a área de transferência!</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
              Cancelar
            </Button>
            {!generatedLink ? (
              <Button onClick={handleGenerateShareLink}>
                <Link className="h-4 w-4 mr-2" />
                Gerar Link
              </Button>
            ) : (
              <Button onClick={handleCopyLink}>
                <Copy className="h-4 w-4 mr-2" />
                Copiar Link
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Enviar por Email
            </DialogTitle>
            <DialogDescription>
              Envie o orçamento por email para o cliente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Para</Label>
              <Input
                value={emailOptions.to.join(', ')}
                onChange={(e) => setEmailOptions({ ...emailOptions, to: e.target.value.split(',').map(e => e.trim()) })}
                placeholder="email@exemplo.com, email2@exemplo.com"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CC (opcional)</Label>
                <Input
                  value={emailOptions.cc?.join(', ') || ''}
                  onChange={(e) => setEmailOptions({ ...emailOptions, cc: e.target.value.split(',').map(e => e.trim()).filter(Boolean) })}
                  placeholder="copia@exemplo.com"
                />
              </div>
              <div>
                <Label>BCC (opcional)</Label>
                <Input
                  value={emailOptions.bcc?.join(', ') || ''}
                  onChange={(e) => setEmailOptions({ ...emailOptions, bcc: e.target.value.split(',').map(e => e.trim()).filter(Boolean) })}
                  placeholder="copia-oculta@exemplo.com"
                />
              </div>
            </div>

            <div>
              <Label>Assunto</Label>
              <Input
                value={emailOptions.subject}
                onChange={(e) => setEmailOptions({ ...emailOptions, subject: e.target.value })}
                placeholder="Assunto do email"
              />
            </div>

            <div>
              <Label>Mensagem</Label>
              <Textarea
                value={emailOptions.message}
                onChange={(e) => setEmailOptions({ ...emailOptions, message: e.target.value })}
                placeholder="Escreva sua mensagem..."
                rows={6}
              />
            </div>

            <div className="space-y-3">
              <Label>Anexos</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Anexar PDF</span>
                  <Switch
                    checked={emailOptions.attachPDF}
                    onCheckedChange={(checked) => setEmailOptions({ ...emailOptions, attachPDF: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Anexar Excel</span>
                  <Switch
                    checked={emailOptions.attachExcel}
                    onCheckedChange={(checked) => setEmailOptions({ ...emailOptions, attachExcel: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Rastrear abertura</span>
                  <Switch
                    checked={emailOptions.trackOpens}
                    onCheckedChange={(checked) => setEmailOptions({ ...emailOptions, trackOpens: checked })}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSendEmail}>
              <Send className="h-4 w-4 mr-2" />
              Enviar Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Dialog */}
      <Dialog open={whatsappDialogOpen} onOpenChange={setWhatsAppDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Enviar por WhatsApp
            </DialogTitle>
            <DialogDescription>
              Envie o orçamento pelo WhatsApp
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Telefone</Label>
              <Input
                value={whatsappOptions.phone}
                onChange={(e) => setWhatsAppOptions({ ...whatsappOptions, phone: e.target.value })}
                placeholder="(00) 00000-0000"
              />
            </div>

            <div>
              <Label>Mensagem</Label>
              <Textarea
                value={whatsappOptions.message}
                onChange={(e) => setWhatsAppOptions({ ...whatsappOptions, message: e.target.value })}
                placeholder="Escreva sua mensagem..."
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Incluir link</span>
                <Switch
                  checked={whatsappOptions.includeLink}
                  onCheckedChange={(checked) => setWhatsAppOptions({ ...whatsappOptions, includeLink: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Incluir PDF</span>
                <Switch
                  checked={whatsappOptions.includePDF}
                  onCheckedChange={(checked) => setWhatsAppOptions({ ...whatsappOptions, includePDF: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Incluir resumo</span>
                <Switch
                  checked={whatsappOptions.includeSummary}
                  onCheckedChange={(checked) => setWhatsAppOptions({ ...whatsappOptions, includeSummary: checked })}
                />
              </div>
            </div>

            {whatsappOptions.includeSummary && (
              <div className="bg-muted/30 rounded-lg p-3">
                <h4 className="font-medium mb-2">Resumo do Orçamento</h4>
                <div className="text-sm space-y-1">
                  <p><strong>Número:</strong> {budget.number}</p>
                  <p><strong>Cliente:</strong> {budget.client.name}</p>
                  <p><strong>Valor Total:</strong> {formatCurrency(budget.total)}</p>
                  <p><strong>Status:</strong> {budget.status}</p>
                  <p><strong>Itens:</strong> {budget.items.length}</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsAppDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSendWhatsApp} disabled={!whatsappOptions.phone}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Enviar WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrCodeDialogOpen} onOpenChange={setQrCodeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code do Orçamento
            </DialogTitle>
            <DialogDescription>
              Compartilhe o orçamento facilmente com QR Code
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-center">
            <div className="bg-muted/30 rounded-lg p-8">
              <div className="w-48 h-48 mx-auto bg-card rounded-lg flex items-center justify-center">
                <QrCode className="h-32 w-32 text-muted-foreground" />
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Escaneie o QR Code para acessar o orçamento
              </p>
              <Button variant="outline" className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Baixar QR Code
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}